import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import Http from "../../Http";
import { Insurance_API } from "../../Helper";
import AdBanner from '../AdPage/AdBanner';
export default function TravelInsuranceCalculator() {
      const [insuranceData, setInsuranceData] = useState([]);
      const [loading, setLoading] = useState(true);
      const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    age: 30,
    days: 7,
    destination: 'medium',
    sumInsured: 5,
    covidCover: false
  });
  
  const [result, setResult] = useState(null);
  const [pieData, setPieData] = useState([]);

  const handleInputChange = (e) => {
    const { id, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [id]: type === 'checkbox' ? checked : ['age', 'days', 'sumInsured'].includes(id) ? parseInt(value) : value
    });
  };

  const handleSliderChange = (e) => {
    const { id, value } = e.target;
    setFormData({
      ...formData,
      [id]: parseInt(value)
    });
  };

  const formatIndianCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(value);
  };

  const calculatePremium = () => {
    const { age, days, destination, sumInsured, covidCover } = formData;
    
    // Base rate based on destination
    let baseRate;
    switch (destination) {
      case 'low': baseRate = 2.5; break;
      case 'medium': baseRate = 3.5; break;
      case 'high': baseRate = 5.0; break;
      default: baseRate = 3.5;
    }
    
    // Calculate base premium
    const basePremium = baseRate * days * sumInsured;
    
    // Add-ons
    const addOn = covidCover ? 300 : 0;
    
    // Calculate subtotal and GST
    const subtotal = basePremium + addOn;
    const gst = subtotal * 0.18;
    
    // Calculate total premium
    const total = subtotal + gst;
    
    // Set result
    const newResult = {
      basePremium,
      addOn,
      subtotal,
      gst,
      total
    };
    
    setResult(newResult);
    
    // Update pie chart data
    setPieData([
      { name: 'Base Premium', value: basePremium },
      { name: 'Add-Ons', value: addOn },
      { name: 'GST', value: gst }
    ]);
  };

  // Calculate on initial render
  useEffect(() => {
    calculatePremium();
  }, []);

  // Calculate whenever form data changes
  useEffect(() => {
    calculatePremium();
  }, [formData]);

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28'];

  const getDestinationLabel = (value) => {
    switch (value) {
      case 'low': return 'Low Risk';
      case 'medium': return 'Medium Risk';
      case 'high': return 'High Risk';
      default: return 'Medium Risk';
    }
  };
  function Icon({ id, open }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={2}
            stroke="currentColor"
            className={`${id === open ? "rotate-180" : ""} h-5 w-5 transition-transform`}
        >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
    );
  }
  
  
    const [activeIndex, setActiveIndex] = useState(0);
  
     
    const faqs = [
        {
            question: 'Is the calculator free to use?',
            answer: 'Yes, it is 100% free and accessible anytime on TheFinmaster.',
          },
          {
            question: 'Do I need to register to use the calculator?',
            answer: 'No registration is required for premium estimation.',
          },
          {
            question: 'Can I purchase a policy directly after using the calculator?',
            answer: 'Yes, you can instantly purchase your selected plan.',
          },
          {
            question: 'What if I have a pre-existing condition?',
            answer: 'Enter this during calculation for accurate premium and coverage eligibility.',
          },
          {
            question: 'Does it work for group policies?',
            answer: 'Yes, it supports group, family, and corporate trip calculations.',
          },
          {
            question: 'What if my trip gets canceled after buying insurance?',
            answer: 'Many policies offer trip cancellation coverage. Check specific plan terms.',
          },
          {
            question: 'Can I include coverage for adventure activities?',
            answer: 'Yes, optional add-ons are available for extreme sports and similar activities.',
          },
          {
            question: 'Will the premium increase if I include my spouse and children?',
            answer: 'Yes, but family floater plans can offer bundled savings.',
          },
          {
            question: 'How often should I calculate before booking?',
            answer: 'Once your trip is confirmed, calculate early for better choices and possible discounts.',
          },
          {
            question: 'Can I calculate premiums for multi-trip plans?',
            answer: 'Absolutely. Just select \'multi-trip\' in the calculator settings.',
          },
          {
            question: 'Do different insurers charge different premiums for the same inputs?',
            answer: 'Yes. That’s why comparison is important.',
          },
          {
            question: 'Can I cancel the policy after buying?',
            answer: 'Most insurers allow cancellation with terms. Check refund policies.',
          },
          {
            question: 'Is it possible to extend the policy if my trip extends?',
            answer: 'Yes, extensions are possible before expiry. Some may require additional premium.',
          },
          {
            question: 'Is there a limit to how much I can insure for?',
            answer: 'Most insurers offer caps. Choose based on travel cost and destination.',
          },
        ];
    const toggleAccordion = (index) => {
      if (activeIndex === index) {
          // If the clicked question is already expanded, collapse it
          setActiveIndex(null);
      } else {
          // If the clicked question is not expanded, expand it
          setActiveIndex(index);
      };
    }
    useEffect(() => {
        fetchInsuranceData();
      }, []);
      const fetchInsuranceData = async () => {
        try {
          const response = await Http.get(Insurance_API);
    
          // The response structure might have nested data
          // Check if response has an insurance property (like in the original fetch response)
          const data = response.insurance || response;
    
          // Filter the insurance data to show only Car category
          const carInsuranceData = Array.isArray(data)
            ? data.filter((insurance) => insurance.category_name === "Travel")
            : [];
    
          setInsuranceData(carInsuranceData);
          setLoading(false);
        } catch (error) {
          setError("Failed to fetch insurance data");
          setLoading(false);
          console.error("Error fetching data:", error);
        }
      };
  return (
    <>
     <Helmet>
         <title>Travel Insurance Calculator – Get Instant Premium Estimates | TheFinmaster</title>
        <meta  name='description' content='Use our free Travel Insurance Calculator to quickly estimate premiums, compare coverage options, and secure the right policy for your domestic or international trips.'/>
        <meta name='keywords' content='rd calculator, rd deposit calculator, rd recurring deposit calculator, recurring deposit cal' />
        <link rel="canonical" href="https://thefinmaster.com/financial-tools-calculators/travel-insurance-calculator" />
        <meta name="robots" content="max-snippet:-1, max-image-preview:standard" />
        <meta name="robots" content="max-video-preview:-1" />
      </Helmet>
      {/* <nav className="text-gray-500 text-sm my-4 ml-10" aria-label="Breadcrumb">
      <ol className="list-none p-0 inline-flex">
        <li className="flex items-center">
          <Link to="/" className="text-blue-600 hover:text-blue-800 ">Home</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
        <li className="flex items-center">
          <Link to="/financial-tools-calculators" className="text-blue-600 hover:text-blue-800">Financial Tools Calculators</Link>
          <svg className="w-4 h-4 mx-2 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L11.586 9 7.293 4.707a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
          </svg>
        </li>
          <li className="flex items-center">
          <span className="text-gray-500 ">Travel  Calculator</span>
        </li> 
      
      </ol>
    </nav> */}
         <div className="w-full flex justify-center items-center px-4 mt-20">
      <div className="w-full max-w-5xl h-auto object-contain rounded-lg shadow-md transition duration-300 hover:scale-105">
      <AdBanner displayOrder="1" />
      </div>
    </div>
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden">
        <div className="p-6 md:p-8">
          <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-8">
            Travel Insurance Premium Calculator
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Form Section */}
            <div className="space-y-6">
              <div>
                <label htmlFor="age" className="block text-gray-700 font-medium mb-2">
                  Traveler's Age
                </label>
                <input
                  type="number"
                  id="age"
                  min="1"
                  max="100"
                  value={formData.age}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
                <input
                  type="range"
                  id="age"
                  min="1"
                  max="100"
                  value={formData.age}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 Yr</span>
                  <span>100 Yr</span>
                </div>
              </div>

              <div>
                <label htmlFor="days" className="block text-gray-700 font-medium mb-2">
                  Trip Duration (Days)
                </label>
                <input
                  type="number"
                  id="days"
                  min="1"
                  max="180"
                  value={formData.days}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
                <input
                  type="range"
                  id="days"
                  min="1"
                  max="180"
                  value={formData.days}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 Day</span>
                  <span>180 Days</span>
                </div>
              </div>

              <div>
                <label htmlFor="destination" className="block text-gray-700 font-medium mb-2">
                  Destination Risk Level
                </label>
                <select
                  id="destination"
                  value={formData.destination}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                >
                  <option value="low">Low Risk</option>
                  <option value="medium">Medium Risk</option>
                  <option value="high">High Risk</option>
                </select>
              </div>

              <div>
                <label htmlFor="sumInsured" className="block text-gray-700 font-medium mb-2">
                  Sum Insured (in Lakhs)
                </label>
                <input
                  type="number"
                  id="sumInsured"
                  min="1"
                  max="50"
                  value={formData.sumInsured}
                  onChange={handleInputChange}
                  className="w-full p-3 border border-gray-300 rounded-lg"
                  required
                />
                <input
                  type="range"
                  id="sumInsured"
                  min="1"
                  max="50"
                  value={formData.sumInsured}
                  onChange={handleSliderChange}
                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer mt-2"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>₹1 Lakh</span>
                  <span>₹50 Lakhs</span>
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="covidCover"
                  checked={formData.covidCover}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="covidCover" className="ml-2 text-gray-700">
                  Add COVID-19 Cover (₹300)
                </label>
              </div>

              <button
                onClick={calculatePremium}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-4 rounded-lg transition duration-300"
              >
                Calculate  Estimated Premium
              </button>
            </div>

            {/* Results Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h2 className="text-xl font-bold text-gray-800 mb-6">Results</h2>
              
              {result && (
                <div className="space-y-6">
                  <div className="bg-white p-5 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Premium Estimated Details</h3>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Traveler's Age:</span>
                        <span className="font-medium">{formData.age} years</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Trip Duration:</span>
                        <span className="font-medium">{formData.days} days</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Destination:</span>
                        <span className="font-medium">{getDestinationLabel(formData.destination)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sum Insured:</span>
                        <span className="font-medium">₹{formData.sumInsured} Lakhs</span>
                      </div>
                      
                      <div className="border-t border-gray-200 my-3"></div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Base Premium:</span>
                        <span className="font-medium">{formatIndianCurrency(result.basePremium)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">Add-Ons:</span>
                        <span className="font-medium">{formatIndianCurrency(result.addOn)}</span>
                      </div>
                      
                      <div className="flex justify-between">
                        <span className="text-gray-600">GST (18%):</span>
                        <span className="font-medium">{formatIndianCurrency(result.gst)}</span>
                      </div>
                      
                      <div className="border-t border-gray-200 my-3"></div>
                      
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-gray-800">Total Estimated Premium:</span>
                        <span className="text-blue-600">{formatIndianCurrency(result.total)}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-5 rounded-lg shadow">
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Premium Estimated Breakdown</h3>
                    <div className="h-64 w-full">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => formatIndianCurrency(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div className="flex flex-wrap lg:px-6 md:px-6 px-0 py-6" >
    <div className="w-full md:w-2/3 p-4 mx-auto">
    <div className="container mx-auto py-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
            What is a Travel Insurance Premium Calculator?
          </h2>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            A Travel Insurance Premium Calculator is an intuitive digital tool that provides travelers with an estimate of the insurance premium they need to pay based on selected travel details. This tool helps users make well-informed insurance decisions by offering transparency, ease of use, and real-time calculations. It eliminates the guesswork from the insurance-buying process and ensures that users receive a quote tailored to their specific journey.
          </p>
          <p className="text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg mt-2">
            <strong>Key Features:</strong>
          </p>
          <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            <li>Instant quote generation</li>
            <li>Plan comparisons from multiple insurers</li>
            <li>Ability to customize coverage options</li>
            <li>User-friendly interface for all age groups</li>
          </ul>
          <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
            It simplifies the buying process by allowing travelers to compare and purchase the most relevant plan without involving agents or undergoing complicated processes.
          </p>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is a Travel Insurance Premium?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The travel insurance premium is the amount you pay to the insurance company to avail financial coverage for unforeseen events during travel. It is a predetermined fee, often paid before the journey begins, and ensures you are protected against emergencies such as:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Medical expenses</li>
              <li>Baggage loss</li>
              <li>Trip cancellations or delays</li>
              <li>Passport and document loss</li>
              <li>Personal accident</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The premium varies depending on several risk factors associated with the traveler and their journey.
            </p>
          </div>
          <div className="container mx-auto px-4">
          <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
          Popular Travel Life Insurance Cards
            </h2>
  <div className="flex flex-col items-center space-y-6 mt-4">
    
  {insuranceData.slice(0, 5).map((insurance) => (
      <div
        key={insurance.id}
        className="w-full max-w-4xl bg-white rounded-2xl shadow hover:shadow-lg transition-shadow duration-300 p-4"
      >
        <div className="flex flex-col md:flex-row items-center md:items-start md:justify-between gap-6">
         
         <Link to={`/travel-insurance/${insurance.page_url}`}
            className="w-full md:w-[180px] flex justify-center md:justify-start"
          >
            <img
              src={insurance.image}
              alt={insurance.page_title || "Insurance Provider"}
              className="h-18 object-contain"
            />
          </Link>

   
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center md:text-left">
            <div>
              <p className="text-gray-500 text-sm font-medium">Premium Starting</p>
              <h4 className="text-lg font-bold">
              ₹{insurance.premium_start}
                
              </h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Baggage Loss</p>
              <h4 className="text-lg font-bold">{insurance.claim_settlement}%</h4>
            </div>
            <div>
              <p className="text-gray-500 text-sm font-medium">Medical expenses</p>
              <h4 className="text-lg font-bold">{insurance.network_garages}+</h4>
            </div>
          </div>


          <div className="flex flex-col sm:flex-row items-center gap-2 mt-4 md:mt-0">
            <a
              href={insurance.bank_link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                Get Quote!
              </button>
            </a>
                 <Link to={`/travel-insurance/${insurance.page_url}`}className="w-full sm:w-auto">
              <button className="bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded w-full sm:w-auto">
                See Details
              </button>
            </Link>
          </div>
        </div>
      </div>
    ))}
  </div>
</div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Use TheFinmaster Travel Insurance Calculator?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using TheFinmaster's Travel Insurance Calculator is simple and effective. Here's how:
            </p>
            <ul className="list-decimal pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Enter Personal and Travel Details:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Age of travelers</li>
                  <li>Travel destination</li>
                  <li>Trip duration</li>
                  <li>Purpose of travel (leisure, business, education)</li>
                </ul>
              </li>
              <li><strong>Choose Plan Type:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Single-trip or multi-trip</li>
                  <li>Individual, family, or group</li>
                </ul>
              </li>
              <li><strong>Customize Coverage:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Sum insured</li>
                  <li>Optional add-ons (adventure sports, home burglary while away, etc.)</li>
                </ul>
              </li>
              <li><strong>Review & Compare Quotes:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Get a list of plans with varying premiums and coverage benefits</li>
                  <li>Compare benefits, features, and premiums side-by-side</li>
                </ul>
              </li>
              <li><strong>Select & Purchase Policy:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Choose the plan that fits your needs</li>
                  <li>Pay securely and get your policy instantly</li>
                </ul>
              </li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              What is the Travel Insurance Premium Formula?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The standard formula for calculating travel insurance premium is:
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Travel Insurance Premium = Base Rate + (Daily Rate × Duration) + Add-ons – Discounts (if any)
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Explanation:</strong>
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Base Rate:</strong> Set by the insurer based on destination and age</li>
              <li><strong>Daily Rate:</strong> Calculated per day of travel</li>
              <li><strong>Add-ons:</strong> Additional features like trip cancellation or adventure coverage</li>
              <li><strong>Discounts:</strong> Early booking, loyalty, or multi-traveler discounts</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Insurers may also use advanced algorithms to factor in risk ratings of countries and the traveler’s health profile.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Information Required for Premium Calculation
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              To receive an accurate quote, you must enter the following:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Name, age, and number of travelers</li>
              <li>Travel destination (country/countries)</li>
              <li>Departure and return dates</li>
              <li>Travel purpose (tourism, business, study)</li>
              <li>Medical history (especially pre-existing conditions)</li>
              <li>Required add-ons or custom coverage</li>
              <li>Sum insured or coverage amount desired</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Providing precise and honest information ensures accurate premium results and avoids claim rejections.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Key Benefits of TheFinmaster Travel Insurance Calculator
            </h2>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Instant Quotes:</strong> No waiting, get real-time premium details</li>
              <li><strong>Customizable Options:</strong> Tailor coverage based on your needs</li>
              <li><strong>Wide Insurer Network:</strong> Compare plans from multiple trusted providers</li>
              <li><strong>Transparent Comparison:</strong> Review benefits and exclusions side-by-side</li>
              <li><strong>Mobile Friendly:</strong> Calculate and buy insurance on the go</li>
              <li><strong>Free to Use:</strong> No service charges or hidden fees</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How is Travel Insurance Premium Determined?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Travel insurance premiums are dynamic and depend on:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Destination Country:</strong> High-risk or expensive countries (like the USA) mean higher premiums</li>
              <li><strong>Trip Length:</strong> Longer trips = more days covered = higher cost</li>
              <li><strong>Traveler’s Age:</strong> Seniors may pay more due to higher health risks</li>
              <li><strong>Medical History:</strong> Pre-existing diseases increase premium</li>
              <li><strong>Sum Insured:</strong> Higher coverage equals a larger premium</li>
              <li><strong>Trip Type:</strong> Adventure, leisure, business – each affects risk assessment</li>
              <li><strong>Add-ons Chosen:</strong> Additional protections raise the base premium</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Reduce Travel Insurance Premium?
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              You can smartly reduce your travel insurance costs by:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Comparing Quotes:</strong> Use TheFinmaster tool to evaluate multiple plans</li>
              <li><strong>Removing Unnecessary Add-ons:</strong> Only pick coverages relevant to your trip</li>
              <li><strong>Early Purchase:</strong> Buy the policy soon after booking your travel</li>
              <li><strong>Opting for Deductibles:</strong> Higher deductibles can reduce premium amount</li>
              <li><strong>Choosing Multi-Trip Plans:</strong> If traveling often, annual multi-trip plans offer better value</li>
              <li><strong>Avoid Over-Insuring:</strong> Select the right sum insured, not the highest possible</li>
            </ul>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Tax Benefits of Travel Insurance
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              While most travel insurance policies do not offer direct tax benefits under Indian tax laws, business travelers can potentially claim the premium as a business expense, reducing taxable income. For employees on official trips, companies may also reimburse the premium.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Always consult a tax advisor to understand applicability based on your individual or business status.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              How to Calculate Travel Insurance Premium for Different Trip Types
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Single-Trip Travel:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Used for one-time journeys. Premium is based on trip length, destination risk, and traveler’s profile.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Multi-Trip Travel:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Annual cover for frequent fliers. Premium is slightly higher but economical over time. Usually covers up to 30-60 days per trip.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Group Travel:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Offers bulk discounts. Suitable for families, student groups, or corporate tours.
            </p>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <strong>Student Travel Insurance:</strong>
            </p>
            <p className="mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Specialized plans for students going abroad. Includes tuition fee loss, study interruption, and sponsor protection.
            </p>
          </div>
          <div className="w-full flex justify-center items-center my-6 px-4">
          <div className="w-full max-w-[984px]">

     
        <AdBanner displayOrder="3" />
        </div>
         
        </div>
          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Travel Insurance for Senior Citizens
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Premiums for senior travelers (typically 60+ years) are calculated differently:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li>Higher base rate due to medical risk</li>
              <li>Often includes mandatory medical tests</li>
              <li>Limited sum insured options</li>
              <li>May restrict adventure or high-risk add-ons</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Using the calculator helps in transparently evaluating suitable plans that cover hospitalization, repatriation, and accidental benefits.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Country-Based Premium Variation
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Destination plays a major role:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>USA/Canada/Europe:</strong> High medical costs = higher premiums</li>
              <li><strong>Asia/SAARC Nations:</strong> Relatively lower premiums</li>
              <li><strong>High-risk Areas (war or natural disasters):</strong> May not be covered at all</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              TheFinmaster calculator auto-adjusts premiums based on global risk indexes and local regulations.
            </p>
          </div>

          <div className="container mx-auto py-4">
            <h2 className="text-xl font-medium bg-blue-100 border-l-4 border-blue-600 pl-4 py-2">
              Claim Settlement Ratio & Insurer Ratings
            </h2>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              Before buying a policy, it’s important to look at:
            </p>
            <ul className="list-disc pl-6 mt-2 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              <li><strong>Claim Settlement Ratio (CSR):</strong> Higher CSR indicates better reliability</li>
              <li><strong>Insurer Ratings:</strong> Check user reviews and complaint resolution statistics</li>
            </ul>
            <p className="mt-4 text-lg text-gray-600 sm:text-xl md:text-lg lg:text-xl xl:text-lg">
              The calculator on TheFinmaster shows top-rated insurers with their CSR and policy benefits.
            </p>
          </div>
        </div>
    
            <div className="w-full md:w-1/3 p-4">
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-blue-100 px-4 py-2 border-b border-blue-600 ">
                  <h2 className="text-lg font-semibold ">Popular Categories</h2>
                </div>
                <div className="p-2 ">
                <ul>
                                    <Link to='/financial-tools-calculators/swp-calculator'>
                                        <li className="py-2  hover:bg-slate-100 border-b border-gray-300">SWP Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/mutul-fund-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Mutual Fund Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/sukanya-samriddhi-yojana-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Samriddhi Yojana Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/fd-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">FD Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/rd-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">RD Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/nps-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NPS Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/hra-exemption-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">HRA Exemption Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/retirement-planning-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Retirement Planning Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/emi-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">EMI Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/simple-interest-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Simple Interest Calculator</li>
    
                                    </Link><Link to='/financial-tools-calculators/compound-interest-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Compound Interest Calculator</li>
    
                                    </Link><Link to='/financial-tools-calculators/nsc-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">NSC Calculator</li>
    
                                    </Link><Link to='/financial-tools-calculators/step-up-sip-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Step Up Sip Calculator</li>
    
                                    </Link><Link to='/financial-tools-calculators/income-tax-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Income Tax Calculator</li>
                                        
                                    </Link><Link to='/financial-tools-calculators/home-loan-emi-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Home Loan Emi Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/car-loan-emi-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Car Loan Emi Calculator</li>
                                    </Link>
                                    {/* {categories.map(category => (
           <Link key={category.id} to={`/category/${category.id}`}>
             <li className="py-2 border-b border-gray-300">{category.name}</li>
           </Link>
         ))} */}
                                </ul>
                            </div>
                        </div>
                           {/* Ad Banner */}
  <div className="mt-4">
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="p-3 w-full h-auto rounded-md object-cover">
        <AdBanner displayOrder="2" />
      </div>
    </div>
  </div>
                        <div className="bg-white shadow-md rounded-lg overflow-hidden mt-4">
                            <div className="bg-blue-100 px-4 py-2 border-b border-blue-600">
                                <h2 className="text-lg font-semibold">More Categories</h2>
                            </div>
                            <div className="p-2">
                            <ul>
                                    <Link to='/financial-tools-calculators/gratuity-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Gratuity Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/APY-calculator'>
                                        <li className="py-2 border- hover:bg-slate-100 border-gray-300">APY Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/cagr-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">CAGR Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/gst-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">GST Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/flat-vs-reducing-rate-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Flat Vs Reducing Rate Calculator</li>
                                    </Link>
                                    {/* <Link to='/financial-tools-calculators/equity-margin-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Equity Margin Calculator</li>
                                    </Link> */}
                                    <Link to='/financial-tools-calculators/inflation-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Inflation Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/salary-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Salary Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/brokerage-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">Brokerage Calculator</li>
                                    </Link>
                                    <Link to='/financial-tools-calculators/tds-calculator'>
                                        <li className="py-2 border-b hover:bg-slate-100 border-gray-300">TDS Calculator</li>
                                    </Link>
                    {/* {moreCategories.map(category => (
              <Link key={category.id} to={`/category/${category.id}`}>
                <li className="py-2 border-b border-gray-300">{category.name}</li>
              </Link>
            ))} */}
                  </ul>
                </div>
              </div>
    
            </div>
    
          </div>
          <div className="max-w-4xl mx-auto lg:px-6 md:px-6 px-2 py-6">
                    <h2 className="text-center text-2xl md:text-2xl lg:text-3xl font-bold mb-8">Frequently Asked Questions</h2>
                    <div className="space-y-4">
                        {faqs.map((faq, index) => (
                            <div key={index} className="border rounded-lg overflow-hidden shadow-md">
                                <button
                                    onClick={() => toggleAccordion(index)}
                                    className="w-full p-4 text-left text-lg font-semibold focus:outline-none flex justify-between items-center"
                                >
                                    {faq.question}
                                    <Icon id={index} open={activeIndex} />
                                </button>
                                {activeIndex === index && (
                                    <div className="p-4 bg-gray-100">
                                        <p className="text-gray-700">{faq.answer}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
    </>
  );
}